import { type PartnerResItem, RecordOrigin } from '@sr/types';

import { getPartnerRecordOrigin } from '../partner/partnerAxis';
import { getPartnerDisplayFields } from '../partner/partnerDisplayFields';

export const getPartnerDisplay = (partner: PartnerResItem) => {
  const partnerInfo = getPartnerProps(partner);

  const nameAndText = joinNameAndDescription(partnerInfo.name, partnerInfo.description);

  let desc = '';

  desc += partnerInfo.address.street;
  desc += partnerInfo.address.street ? ', ' : '';
  desc += partnerInfo.address.city;

  // Datalake-shaped records nest delivery addresses under their business partner, so the
  // id shown is the parent's rather than the address's own.
  const isNestedDeliveryAddress =
    getPartnerRecordOrigin(partner) !== RecordOrigin.EDW && partner.type === 'DELIVERYADRESS';

  const bpId = isNestedDeliveryAddress ? getParentBPId(partner.dwKey) : partner.id;

  if (bpId) {
    desc += ` (${bpId})`;
  }

  return {
    nameAndText,
    addressAndId: desc,
    bpId,
  };
};

const PLACEHOLDER_DESCRIPTION = 'N/A';

const comparable = (text: string) => text.replace('(s)', '').trim().toLowerCase();

/**
 * "name, description". A description that starts with the name (ignoring case and "(s)")
 * is returned alone; an empty or "N/A" description returns the name.
 */
export const joinNameAndDescription = (name: string, description?: string | null) => {
  const trimmedName = name.trim();
  const trimmedDescription = description?.trim() ?? '';

  if (!trimmedDescription || trimmedDescription === PLACEHOLDER_DESCRIPTION) {
    return trimmedName;
  }

  if (comparable(trimmedDescription).startsWith(comparable(trimmedName))) {
    return trimmedDescription;
  }

  return `${trimmedName}, ${trimmedDescription}`;
};

export const getParentBPId = (partnerId: string) => {
  const [, bpId] = partnerId.split('_');

  return bpId;
};

export const getPartnerProps = (res: PartnerResItem) => {
  const { name, description, street } = getPartnerDisplayFields(res);

  return {
    id: getParentBPId(res.dwKey),
    name,
    description,
    address: {
      street,
      city: res.city,
      zipcode: res.postalCode,
    },
  };
};

export * from './region';
export * from './serviceFilters';
